import React from 'react';
import { Link } from '@inertiajs/react';
import { ChevronRight, FileText, Users, Home, BookOpen, Calendar, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type Props = {
  title: string;
  children: React.ReactNode;
  breadcrumbs?: { label: string; href?: string }[];
  showBreadcrumbs?: boolean
};

export function AdminLayout({ title, children, breadcrumbs = [], showBreadcrumbs = true }: Props) {
  const defaultBreadcrumbs = [
    { label: 'Admin', href: '/admin' },
    ...breadcrumbs,
    { label: title },
  ];

  return (
    <div className="min-h-screen ">
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Link href="/admin" className="text-xl font-semibold text-gray-900">
                  Admin Panel
                </Link>
              </div>
              <div className="hidden sm:ml-8 sm:flex sm:space-x-8">
                <Link
                  href="/admin"
                  className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-900 border-b-2 border-transparent hover:border-gray-300"
                >
                  <Home className="w-4 h-4 mr-1" />
                  Dashboard
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger className='inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-900 border-b-2 border-transparent hover:border-gray-300'>
                    <Users className='w-4 h-4 mr-1' />
                    Users
                    <ChevronDown className='w-3 h-3 ml-1' />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem asChild>
                      <Link href='/admin/users' className='cursor-pointer'>User Management</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href='/admin/matches' className='cursor-pointer'>Matches</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href='/admin/mentee_pool' className='cursor-pointer'>Mentee Pool</Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Link
                  href="/admin/questionnaire_templates"
                  className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-900 border-b-2 border-transparent hover:border-gray-300"
                >
                  <FileText className="w-4 h-4 mr-1" />
                  Applications
                </Link>
                <Link
                  href="/admin/campaigns"
                  className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-900 border-b-2 border-transparent hover:border-gray-300"
                >
                  <Calendar className="w-4 h-4 mr-1" />
                  Campaigns
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger className='inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-900 border-b-2 border-transparent'>
                    <BookOpen className='w-4 h-4 mr-1' />
                    Content
                    <ChevronDown className='w-3 h-3 ml-1' />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem asChild>
                      <Link href="/admin/board_members" className='cursor-pointer'>Board Members</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/admin/resources" className='cursor-pointer'>Resources</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/admin/faqs" className='cursor-pointer'>FAQs</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/admin/events" className='cursor-pointer'>Events</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/admin/documents" className='cursor-pointer'>Documents</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/admin/fee_waiver_partners" className='cursor-pointer'>Fee Waiver Partners</Link>
                    </DropdownMenuItem>

                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            {/* <div className="flex items-center"> */}
            {/*   <Link */}
            {/*     href="/" */}
            {/*     className="text-sm text-gray-500 hover:text-gray-700" */}
            {/*   > */}
            {/*     ← Back to Site */}
            {/*   </Link> */}
            {/* </div> */}
          </div>
        </div>
      </nav>

      {/* Breadcrumbs */}
      {showBreadcrumbs && defaultBreadcrumbs.length > 1 && (
        <nav className="bg-white border-b border-gray-200" aria-label="Breadcrumb">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ol className="flex items-center space-x-2 py-3">
              {defaultBreadcrumbs.map((crumb, index) => (
                <li key={index} className="flex items-center">
                  {index > 0 && (
                    <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                  )}
                  {crumb.href ? (
                    <Link
                      href={crumb.href}
                      className="text-sm font-medium text-gray-500 hover:text-gray-700"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-sm font-medium text-gray-900">
                      {crumb.label}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </nav>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
}
